import { UNIVERSE, SystemInfo } from '@deepwake/shared';
import { ClientSim } from './sim.js';

const KIND_COLORS: Record<string, string> = {
  sun: '#ffd27a',
  'planet-gas': '#d9a76a',
  'planet-rock': '#b08968',
  'planet-ice': '#a8d8f0',
  station: '#9fd4ff',
  gate: '#c9a7ff',
  belt: '#d9c08a',
  ship: '#ffffff',
  npc: '#ff7a6a',
  wreck: '#8a7462',
  asteroid: '#77706a',
};

export interface MapCallbacks {
  onSelect: (ballId: number) => void;
  onWarp: (ballId: number) => void;
}

/** System map + Reach star map, drawn on a canvas overlay. */
export class Maps {
  el: HTMLElement;
  canvas: HTMLCanvasElement;
  mode: 'system' | 'reach' | null = null;
  private currentSystem: SystemInfo | null = null;
  private hit: { x: number; y: number; id: number; name: string }[] = [];
  private lastClick = 0;
  private lastClickId = -1;

  constructor(private cb: MapCallbacks) {
    this.el = document.createElement('div');
    this.el.id = 'mappanel';
    this.el.className = 'panel hidden';
    this.el.innerHTML = `
      <div class="map-head">
        <span class="ptitle" id="map-title">Map</span>
        <span class="sub" id="map-hint">click: select · double-click: warp</span>
        <button id="map-close">✕</button>
      </div>
      <canvas id="mapcanvas"></canvas>`;
    document.body.appendChild(this.el);
    this.canvas = this.el.querySelector('#mapcanvas') as HTMLCanvasElement;
    this.el.querySelector('#map-close')!.addEventListener('click', () => this.hide());
    this.canvas.addEventListener('click', (e) => this.click(e));

    const btnSys = document.createElement('button');
    btnSys.id = 'mapbtn';
    btnSys.textContent = 'Map';
    btnSys.addEventListener('click', () => this.toggle('system'));
    document.body.appendChild(btnSys);
    const btnReach = document.createElement('button');
    btnReach.id = 'reachbtn';
    btnReach.textContent = 'The Reach';
    btnReach.addEventListener('click', () => this.toggle('reach'));
    document.body.appendChild(btnReach);
  }

  setSystem(info: SystemInfo): void {
    this.currentSystem = info;
  }

  toggle(mode: 'system' | 'reach'): void {
    if (this.mode === mode) return this.hide();
    this.mode = mode;
    this.el.classList.remove('hidden');
    (this.el.querySelector('#map-title') as HTMLElement).textContent =
      mode === 'system' ? `${this.currentSystem?.name ?? 'System'} — tactical` : 'The Reach';
    (this.el.querySelector('#map-hint') as HTMLElement).textContent =
      mode === 'system' ? 'click: select · double-click: warp' : 'your position is ringed';
  }

  hide(): void {
    this.mode = null;
    this.el.classList.add('hidden');
  }

  private click(e: MouseEvent): void {
    if (this.mode !== 'system') return;
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let best: (typeof this.hit)[number] | null = null;
    let bestD = 16;
    for (const h of this.hit) {
      const d = Math.hypot(h.x - x, h.y - y);
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    if (!best) return;
    const now = performance.now();
    if (best.id === this.lastClickId && now - this.lastClick < 450) {
      this.cb.onWarp(best.id);
      this.hide();
    } else {
      this.cb.onSelect(best.id);
    }
    this.lastClick = now;
    this.lastClickId = best.id;
  }

  /** Redraw if visible. Cheap; call a few times a second. */
  draw(sim: ClientSim): void {
    if (!this.mode) return;
    const c = this.canvas;
    const w = (c.width = c.clientWidth * devicePixelRatio);
    const h = (c.height = c.clientHeight * devicePixelRatio);
    const g = c.getContext('2d')!;
    g.scale(devicePixelRatio, devicePixelRatio);
    const cw = c.clientWidth;
    const ch = c.clientHeight;
    g.clearRect(0, 0, cw, ch);
    void w;
    void h;

    if (this.mode === 'system') this.drawSystem(sim, g, cw, ch);
    else this.drawReach(g, cw, ch);
  }

  private drawSystem(sim: ClientSim, g: CanvasRenderingContext2D, cw: number, ch: number): void {
    // Collect globals + own ship; project x/z plane
    const items: { id: number; x: number; z: number; kind: string; name: string }[] = [];
    for (const [id, ball] of sim.park.balls) {
      const deco = sim.deco.get(id);
      if (!deco) continue;
      if (!ball.isGlobal && id !== sim.myBallId) continue;
      items.push({ id, x: ball.newPos.x, z: ball.newPos.z, kind: id === sim.myBallId ? 'ship' : deco.kind, name: deco.name ?? '' });
    }
    let maxR = 1;
    for (const it of items) maxR = Math.max(maxR, Math.hypot(it.x, it.z));
    const scale = (Math.min(cw, ch) * 0.44) / maxR;
    const px = (x: number) => cw / 2 + x * scale;
    const pz = (z: number) => ch / 2 + z * scale;

    // Orbit guide rings
    g.strokeStyle = 'rgba(110,150,200,0.12)';
    for (let i = 1; i <= 4; i++) {
      g.beginPath();
      g.arc(cw / 2, ch / 2, (Math.min(cw, ch) * 0.44 * i) / 4, 0, Math.PI * 2);
      g.stroke();
    }

    this.hit = [];
    for (const it of items) {
      const x = px(it.x);
      const y = pz(it.z);
      const color = KIND_COLORS[it.kind] ?? '#8fa2b8';
      g.fillStyle = color;
      const r = it.kind === 'sun' ? 7 : it.kind.startsWith('planet') ? 5 : it.kind === 'ship' ? 4 : 3.5;
      g.beginPath();
      if (it.kind === 'gate') {
        g.moveTo(x, y - 5);
        g.lineTo(x + 5, y);
        g.lineTo(x, y + 5);
        g.lineTo(x - 5, y);
      } else {
        g.arc(x, y, r, 0, Math.PI * 2);
      }
      g.fill();
      if (it.kind === 'ship') {
        g.strokeStyle = '#fff';
        g.beginPath();
        g.arc(x, y, 8, 0, Math.PI * 2);
        g.stroke();
      }
      if (it.kind !== 'sun' && it.name) {
        g.fillStyle = 'rgba(200,215,235,0.75)';
        g.font = '10px system-ui';
        g.fillText(it.name, x + 8, y + 3);
      }
      if (it.id !== sim.myBallId) this.hit.push({ x, y, id: it.id, name: it.name });
    }
  }

  private drawReach(g: CanvasRenderingContext2D, cw: number, ch: number): void {
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    for (const s of UNIVERSE) {
      minX = Math.min(minX, s.mx);
      maxX = Math.max(maxX, s.mx);
      minY = Math.min(minY, s.my);
      maxY = Math.max(maxY, s.my);
    }
    const pad = 46;
    const px = (x: number) => pad + ((x - minX) / (maxX - minX)) * (cw - pad * 2);
    const py = (y: number) => pad + ((y - minY) / (maxY - minY)) * (ch - pad * 2);

    // Links
    g.strokeStyle = 'rgba(140,170,220,0.25)';
    for (const s of UNIVERSE) {
      for (const gate of s.gates) {
        const o = UNIVERSE.find((u) => u.id === gate.toSystem);
        if (!o || o.id < s.id) continue;
        g.beginPath();
        g.moveTo(px(s.mx), py(s.my));
        g.lineTo(px(o.mx), py(o.my));
        g.stroke();
      }
    }
    // Systems
    for (const s of UNIVERSE) {
      const color = s.security === 'core' ? '#6fd47b' : s.security === 'verge' ? '#e8c35a' : '#e06a5a';
      const x = px(s.mx);
      const y = py(s.my);
      g.fillStyle = color;
      g.beginPath();
      g.arc(x, y, 4.5, 0, Math.PI * 2);
      g.fill();
      if (this.currentSystem?.id === s.id) {
        g.strokeStyle = '#fff';
        g.lineWidth = 1.6;
        g.beginPath();
        g.arc(x, y, 9, 0, Math.PI * 2);
        g.stroke();
        g.lineWidth = 1;
      }
      g.fillStyle = 'rgba(200,215,235,0.85)';
      g.font = '10px system-ui';
      g.fillText(s.name, x + 8, y + 3);
    }
    // Region labels
    g.font = '12px system-ui';
    g.fillStyle = 'rgba(140,165,195,0.5)';
    g.fillText('HEARTHLIGHT', px(14) - 30, 22);
    g.fillText('THE VERGE', px(58) - 24, 22);
    g.fillText('THE DEEP', px(100) - 22, 22);
  }
}
